// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Changelog Modal (Nueva Edición)
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { C } from '../../../../../lib/design';
import { useI18n } from '../../../../../i18n';

export interface ChangelogModalProps {
  open: boolean;
  loading: boolean;
  onConfirm: (descripcion: string) => void;
  onCancel: () => void;
}

export function ChangelogModal({ open, loading, onConfirm, onCancel }: ChangelogModalProps) {
  const { t } = useI18n();
  const [descripcion, setDescripcion] = useState('');
  const [touched, setTouched] = useState(false);

  if (!open) return null;

  const isEmpty = descripcion.trim().length === 0;
  const showError = touched && isEmpty;

  const handleConfirm = () => {
    setTouched(true);
    if (isEmpty) return;
    onConfirm(descripcion.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div
      onClick={onCancel}
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 480,
          maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px 16px',
            borderBottom: '1px solid #E2E6ED',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: `linear-gradient(135deg, ${C.azul1}, ${C.celeste})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 14,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            +
          </div>
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: C.azul1,
                fontFamily: "'Source Serif 4',Georgia,serif",
              }}
            >
              {t.changelog.nuevaEdicion}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: C.textSec }}>
              {t.changelog.describaCambios}
            </p>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 600,
              color: C.text,
              marginBottom: 6,
            }}
          >
            {t.changelog.descripcionCambios} <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <textarea
            value={descripcion}
            onChange={(e) => {
              setDescripcion(e.target.value);
              setTouched(true);
            }}
            placeholder={t.common.exampleChangelog}
            rows={4}
            autoFocus
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: `1.5px solid ${showError ? '#EF4444' : '#E2E6ED'}`,
              fontSize: 13,
              fontFamily: 'Inter,system-ui,sans-serif',
              color: C.text,
              resize: 'vertical',
              outline: 'none',
              transition: 'border-color 0.15s',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              if (!showError) e.currentTarget.style.borderColor = C.celeste;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor =
                touched && descripcion.trim().length === 0 ? '#EF4444' : '#E2E6ED';
            }}
          />
          {showError && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#EF4444' }}>
              {t.changelog.obligatorio}
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 24px 20px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: '8px 18px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              border: '1px solid #E2E6ED',
              background: 'white',
              color: C.textSec,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {t.common.cancelar}
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || (touched && isEmpty)}
            style={{
              padding: '8px 18px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              border: 'none',
              background: C.azul1,
              color: 'white',
              cursor: loading || (touched && isEmpty) ? 'not-allowed' : 'pointer',
              opacity: loading || (touched && isEmpty) ? 0.6 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            {loading ? t.changelog.creando : t.changelog.crearEdicion}
          </button>
        </div>
      </div>
    </div>
  );
}
